import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { Card, IconCircle, PageHeader, SectionLabel } from '@/components/ui';
import { BalanceCard, ExpenseRow } from '@/components/ledger';
import { getBalance, listExpenses, listSettlements } from '@/lib/api';
import type { BalanceResponse } from '@/lib/api';
import { monthYear, shortDate, todayISO } from '@/lib/dates';
import { fmtMoney, safeCurrency } from '@/lib/money';
import { displayName } from '@/lib/people';
import { useApp } from '@/App';
import { baseAmountOf } from '@core/ledger-core';
import type { Expense, Settlement } from '@core/ledger-core';

// Ledger home — mobile app/index.tsx on the web. A balance card phrased
// from the viewer's side ("Liz owes you" in green, "You owe Jake" in
// red) with Add expense + Settle up inside it, then the rows: an
// "Upcoming" group for anything dated after today (soonest first), then
// month groups newest first. Each expense row says what it did to the
// viewer ("you lent $X" / "you borrowed $X"). Expense rows open the
// editor; settlement rows open the payment editor, where Delete lives.
//
// TWO CURRENCIES ON SCREEN AT ONCE (README §Money and multi-currency):
//   - A ROW's headline figure is the NATIVE amount — what the bill
//     actually said, formatMoney(amountCents, record.currency). ¥48,000
//     reads "¥48,000" here and nowhere reads "$48,000".
//   - Everything DERIVED from the balance — the card, the month totals,
//     the per-row "you lent / you borrowed" line — is in the household
//     BASE currency, because that is the only currency those numbers
//     can be added up in. A foreign row shows its base amount as a small
//     line under the native one. The two are never formatted with each
//     other's symbol.

type LedgerRow =
  | { kind: 'expense'; record: Expense }
  | { kind: 'settlement'; record: Settlement };

interface RowGroup {
  key: string;
  label: string;
  rows: LedgerRow[];
}

// Upcoming (date > today, soonest first), then months newest first.
// createdAt breaks same-day ties so the order is stable.
function groupRows(expenses: Expense[], settlements: Settlement[], today: string): RowGroup[] {
  const rows: LedgerRow[] = [
    ...expenses
      .filter((e) => e.deleted !== true)
      .map((record): LedgerRow => ({ kind: 'expense', record })),
    ...settlements
      .filter((s) => s.deleted !== true)
      .map((record): LedgerRow => ({ kind: 'settlement', record })),
  ];
  const upcoming = rows
    .filter((r) => r.record.date > today)
    .sort(
      (a, b) =>
        a.record.date.localeCompare(b.record.date) ||
        (a.record.createdAt ?? '').localeCompare(b.record.createdAt ?? ''),
    );
  const past = rows
    .filter((r) => r.record.date <= today)
    .sort(
      (a, b) =>
        b.record.date.localeCompare(a.record.date) ||
        (b.record.createdAt ?? '').localeCompare(a.record.createdAt ?? ''),
    );
  const groups: RowGroup[] = [];
  if (upcoming.length > 0) groups.push({ key: 'upcoming', label: 'Upcoming', rows: upcoming });
  const byMonth = new Map<string, RowGroup>();
  for (const row of past) {
    const key = row.record.date.slice(0, 7);
    let g = byMonth.get(key);
    if (!g) {
      g = { key, label: monthYear(key), rows: [] };
      byMonth.set(key, g);
      groups.push(g);
    }
    g.rows.push(row);
  }
  return groups;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function Ledger() {
  const { me, household } = useApp();
  const hid = household.householdId;
  const isOwner = household.role === 'owner';

  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [expenses, setExpenses] = useState<Expense[] | null>(null);
  const [settlements, setSettlements] = useState<Settlement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBalance(null);
    setExpenses(null);
    setSettlements(null);
    setError(null);
    Promise.all([getBalance(hid), listExpenses(hid), listSettlements(hid)])
      .then(([b, e, s]) => {
        if (cancelled) return;
        setBalance(b);
        setExpenses(e.expenses);
        setSettlements(s.settlements);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the ledger.');
      });
    return () => {
      cancelled = true;
    };
  }, [hid]);

  const memberEmails = useMemo(
    () => household.members.map((m) => m.email),
    [household.members],
  );

  const groups = useMemo(
    () => (expenses && settlements ? groupRows(expenses, settlements, todayISO()) : null),
    [expenses, settlements],
  );

  // The currency every balance-derived figure on this screen is in. The
  // server names it on the balance envelope; household.currency (from
  // /api/me) is the fallback for a response that predates the field.
  const baseCurrency = safeCurrency(balance?.baseCurrency, household.currency);

  const pair = balance?.pairwise[0] ?? null;

  return (
    <AppShell>
      <PageHeader title={household.name} />

      <BalanceCard
        pair={pair}
        me={me.email}
        baseCurrency={baseCurrency}
        isOwner={isOwner}
        loading={balance === null}
        error={error}
      />

      {groups !== null && groups.length === 0 && (
        <Card className="section-gap">
          <div className="empty">
            <IconCircle icon="add" tone="accent" iconSize={22} />
            <h2 className="empty-title">No expenses yet</h2>
            <p className="empty-body">
              {isOwner ? 'Tap Add expense to log the first one.' : 'Nothing has been added yet.'}
            </p>
          </div>
        </Card>
      )}

      {groups === null && !error && <p className="loading">Loading…</p>}

      {groups?.map((g) => {
        // Base units: a month can hold ¥ and $ rows, and only their base
        // amounts add up. Rendered with baseCurrency to match.
        const spent = g.rows.reduce(
          (sum, r) => (r.kind === 'expense' ? sum + baseAmountOf(r.record) : sum),
          0,
        );
        return (
          <section key={g.key} aria-labelledby={`group-${g.key}`}>
            <SectionLabel
              id={`group-${g.key}`}
              aside={g.key !== 'upcoming' && spent > 0 ? `${fmtMoney(spent, baseCurrency)} spent` : undefined}
            >
              {g.label}
            </SectionLabel>
            <Card>
              {g.rows.map((row) =>
                row.kind === 'expense' ? (
                  <ExpenseRow
                    key={row.record.id}
                    expense={row.record}
                    baseCurrency={baseCurrency}
                    memberEmails={memberEmails}
                    me={me.email}
                  />
                ) : (
                  <SettlementRow
                    key={row.record.id}
                    settlement={row.record}
                    baseCurrency={baseCurrency}
                    me={me.email}
                  />
                ),
              )}
            </Card>
          </section>
        );
      })}
    </AppShell>
  );
}

// Money the viewer RECEIVED reads green with a "+"; money they sent
// reads in ink (it isn't "owed to you"). A spectator sees ink.
function SettlementRow({
  settlement,
  baseCurrency,
  me,
}: {
  settlement: Settlement;
  baseCurrency: string;
  me: string;
}) {
  const code = safeCurrency(settlement.currency, baseCurrency);
  const received = settlement.to === me;
  const method = settlement.method ? capitalize(settlement.method) : 'Payment';
  return (
    <Link to={`/settle/${encodeURIComponent(settlement.id)}`} className="row">
      <IconCircle icon="transfer" tone={received ? 'owed' : 'neutral'} />
      <span className="row-body">
        <span className="row-title">
          {displayName(settlement.from)} paid {displayName(settlement.to)}
        </span>
        <span className="row-meta">
          {method} · {shortDate(settlement.date)}
        </span>
      </span>
      <span className="row-end">
        <span className={`row-amount${received ? ' is-owed' : ''}`}>
          {received ? '+' : ''}
          {fmtMoney(settlement.amountCents, code)}
        </span>
        {code !== baseCurrency && (
          <span className="row-sub">{fmtMoney(baseAmountOf(settlement), baseCurrency)}</span>
        )}
        <span className="row-impact">
          {received ? 'received' : me === settlement.from ? 'you paid' : 'payment'}
        </span>
      </span>
    </Link>
  );
}
